import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import {
  Linkedin,
  Globe,
  Calendar,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
} from "lucide-react";
import { subscribeEmail, fetchDemoCard } from "../utils/api";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Stat,
  Hairline,
  FootnoteList,
  SEO,
  Card,
} from "./ui";
import HeroHandlePicker from "./HeroHandlePicker";
import { canonicalFor } from "../routes";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Do I need an app to share my card?",
    a: "No. The recipient just opens the link. Tap an NFC card to any modern phone, scan the printed QR, or text the URL. WhoBrew loads in the browser. No download, no install, no friction. The same link works on iPhone, Android, desktop, and inside iMessage, WhatsApp, and Slack previews.",
  },
  {
    q: "What happens when I change jobs?",
    a: "You update your card once. Every share you've ever made — past, present, or future — points to the new info. The link itself never changes. Old paper cards are dead the day you change jobs. WhoBrew cards aren't.",
  },
  {
    q: "How does the physical NFC card actually work?",
    a: "It's a thin PVC card with an NFC chip embedded inside and a printed QR code on the back. Hold it within an inch of someone's phone, and your WhoBrew page opens in their browser. Works with iPhone 7 and later, every Android from the last 8 years, and the QR is a backup for older phones. No app on either side.",
  },
  {
    q: "Can I track who views my card?",
    a: "Yes, with a privacy-first approach. Insights show total visits, link clicks, devices, geography, and which cards drive saves, with no third-party tracker and no personal identifiers stored. You see counts, not contact lists, unless someone deliberately fills out your contact form.",
  },
  {
    q: "Is there a free tier?",
    a: "Yes. Starter is free forever and covers one card with five links. Pro is $4.99 a month, no annual contract, and adds analytics, multiple cards, and lead capture. The physical NFC card is a one-time $14.99 purchase that works on any plan, including Free.",
  },
];

const FOOTNOTES = [
  { id: 3, text: "Compatible with NFC-enabled phones from 2017 onward; QR fallback covers everything older." },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "WhoBrew",
  url: "https://whobrew.us",
  logo: "https://whobrew.us/logo512.png",
  sameAs: ["https://www.facebook.com/whobrew.us"],
};

const SOFTWARE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "WhoBrew",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: [
    { "@type": "Offer", name: "Starter", price: "0",    priceCurrency: "USD" },
    { "@type": "Offer", name: "Pro",     price: "4.99", priceCurrency: "USD" },
  ],
};

function Landing() {
  const { isSignedIn } = useUser();
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://whobrew.us";

  return (
    <>
      <SEO
        title="WhoBrew — Digital business cards"
        description="One link with your contact, calendar, and pitch. Share it by URL or QR. Update once, never reprint. Free forever, $4.99/mo Pro."
        canonical={canonicalFor("/")}
        jsonLd={[ORG_JSONLD, SOFTWARE_JSONLD, FAQ_JSONLD]}
      />

      {/* HERO — Editorial */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>A business card you update from your phone</Eyebrow>

              <Display as="h1" scale="xl" italicWord="Digital" className="mt-5">
                Digital business cards that update themselves.
              </Display>

              <p className="mt-7 text-lg sm:text-xl text-coffee-700 dark:text-cream-200 max-w-xl leading-relaxed">
                A single link with your name, your calendar, and whatever you want people to see
                today. Share it by URL or QR — update once, never reprint.
              </p>

              <div className="mt-8 max-w-xl">
                <HeroHandlePicker origin={origin} />
              </div>

              <p className="mt-5 font-mono text-caption text-coffee-600 dark:text-cream-200">
                Free forever tier. No app required. Works on iPhone 7 and every Android from the
                last 8 years.<sup><a href="#fn-3" className="text-accent-500 no-underline">3</a></sup>
              </p>
            </div>

            {/* Live data product element */}
            <div className="lg:col-span-5">
              <PreviewCard />
            </div>
          </div>
        </Container>
      </Section>

      {/* STAT BAND */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            <Stat value="60s" caption="From sign-up to a card you can share." />
            <Stat value="Free" caption="Starter is free forever. No credit card to start." />
            <Stat value="$14.99" caption="Optional NFC card. Adds tap-to-open on any plan." />
          </div>
        </Container>
      </Section>

      {/* FEATURE ROW 1 — Always current */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 lg:order-2">
              <Eyebrow step={1} of={4}>Always current</Eyebrow>
              <Display as="h2" scale="md" italicWord="once" className="mt-4">
                Update once. Every old card updates with it.
              </Display>
              <p className="mt-5 text-body text-coffee-700 dark:text-cream-200 max-w-md">
                The link never changes. The job title does, the calendar does, the company does.
                Edit your card and every share you've ever sent points to the new version, the
                same minute.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Up to three cards per Pro account, one per role.",
                  "Eleven themes with editable accent and surface colors.",
                  "CSV export for leads and analytics, on demand.",
                ].map((b) => (
                  <li key={b} className="flex gap-3 text-body-sm text-coffee-700 dark:text-cream-200">
                    <span className="text-accent-500 font-mono shrink-0">·</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7 lg:order-1">
              <Card tone="raised" className="overflow-hidden">
                <BrowserChrome url="whobrew.us/my-card" />
                <DashboardMockup />
              </Card>
            </div>
          </div>
        </Container>
      </Section>

      {/* FEATURE ROW 2 — Insights */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <Eyebrow step={2} of={4}>Visit analytics</Eyebrow>
              <Display as="h2" scale="md" italicWord="Counts" className="mt-4">
                Counts. Not contacts.
              </Display>
              <p className="mt-5 text-body text-coffee-700 dark:text-cream-200 max-w-md">
                Per-card visits. Per-link clicks. Devices, cities, time of day. No third-party
                tracker, no personal identifiers, no surprise. You see what got clicked, not
                who clicked it.
              </p>
            </div>
            <div className="lg:col-span-7">
              <Card tone="flat" className="overflow-hidden border border-latte-300 dark:border-latte-800">
                <BrowserChrome url="whobrew.us/insights" />
                <InsightsMockup />
              </Card>
            </div>
          </div>
        </Container>
      </Section>

      {/* COMPARISON INLINE — Paper vs WhoBrew */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-14">
            <Eyebrow>The honest comparison</Eyebrow>
            <Display as="h2" scale="md" italicWord="paper" className="mt-4">
              Paper does some things well. Just not many of them.
            </Display>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            <div className="bg-cream-100 dark:bg-coffee-800 p-8 sm:p-10">
              <Eyebrow>Paper card</Eyebrow>
              <Display as="h3" scale="sm" className="mt-3">
                The old way.
              </Display>
              <ul className="mt-6 space-y-3 text-body-sm text-coffee-700 dark:text-cream-200">
                <ComparisonRow text="Three to five days to print and ship." />
                <ComparisonRow text="$50–$100 a box, every reorder." />
                <ComparisonRow text="One name, one role, one phone number." />
                <ComparisonRow text="Useless the day your title changes." />
                <ComparisonRow text="No idea who actually saved one." />
              </ul>
            </div>
            <div className="bg-coffee-900 dark:bg-cream-100 text-cream-50 dark:text-coffee-900 p-8 sm:p-10">
              <Eyebrow>WhoBrew</Eyebrow>
              <Display as="h3" scale="sm" className="mt-3 text-cream-50 dark:text-coffee-900">
                The new way.
              </Display>
              <ul className="mt-6 space-y-3 text-body-sm">
                <ComparisonRow inverted text="Live the moment you save." />
                <ComparisonRow inverted text="Free forever, $4.99 a month for Pro." />
                <ComparisonRow inverted text="Calendar, deck, listings, anything." />
                <ComparisonRow inverted text="Update once, every share updates with it." />
                <ComparisonRow inverted text="See visits, clicks, and saves." />
              </ul>
            </div>
          </div>
        </Container>
      </Section>

      {/* USE CASES */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-14">
            <Eyebrow step={3} of={4}>Use cases</Eyebrow>
            <Display as="h2" scale="md" italicWord="card" className="mt-4">
              Pick your role. There's a card for it.
            </Display>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-latte-300 dark:bg-latte-800 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            {[
              { tag: "Recruiters",  body: "Career fairs, open roles, candidate intake.",   to: "/for/recruiters" },
              { tag: "Sales",       body: "Demos, decks, lead capture.",                    to: "/for/sales" },
              { tag: "Real estate", body: "Listings, tours, sign-in QR.",                   to: "/for/real-estate" },
              { tag: "Founders",    body: "Press kit, deck, calendar in one link.",         to: "/for/founders" },
            ].map((c) => (
              <a
                key={c.tag}
                href={c.to}
                className="group bg-cream-50 dark:bg-coffee-900 p-8 hover:bg-cream-100 dark:hover:bg-coffee-800 transition-colors"
              >
                <Display as="h3" scale="sm" className="text-2xl">
                  {c.tag}<span className="text-accent-500">.</span>
                </Display>
                <p className="mt-3 text-body-sm text-coffee-700 dark:text-cream-200">{c.body}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-body-sm font-medium text-coffee-900 dark:text-cream-50 group-hover:gap-2 transition-all">
                  Read <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </a>
            ))}
          </div>
        </Container>
      </Section>

      {/* PRICING TEASER */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-14">
            <Eyebrow step={4} of={4}>Pricing</Eyebrow>
            <Display as="h2" scale="md" italicWord="honest" className="mt-4">
              Three tiers. Honest pricing.
            </Display>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-latte-300 dark:bg-latte-800 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            <PricingTile
              name="Starter"
              price="Free"
              caption="One card, five links, basic insights."
            />
            <PricingTile
              name="Pro"
              price="$4.99"
              suffix="/month"
              caption="Three cards, ten links, lead capture, custom themes, full insights."
            />
            <PricingTile
              name="NFC card"
              price="$14.99"
              suffix=" once"
              caption="A thin PVC card with an embedded NFC chip and printed QR. Works on any plan."
            />
          </div>
          <div className="mt-10 flex justify-center">
            <Button to="/pricing" variant="secondary" trailingIcon={<ArrowRight className="w-4 h-4" />}>
              See full pricing
            </Button>
          </div>
        </Container>
      </Section>

      {/* FAQ — long-form */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Common questions</Eyebrow>
          <Display as="h2" scale="md" italicWord="works" className="mt-4 mb-12">
            How it works, what it costs, what happens next.
          </Display>
          <div className="divide-y divide-latte-300 dark:divide-latte-800 border-y border-latte-300 dark:border-latte-800">
            {FAQ.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => setOpenFAQ(open ? null : i)}
                    aria-expanded={open}
                    className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                  >
                    <span className="font-display text-headline text-coffee-900 dark:text-cream-50 leading-snug">
                      {item.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 mt-2 text-coffee-700 dark:text-cream-200 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div className="pb-8 text-body text-coffee-700 dark:text-cream-200 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <FootnoteList notes={FOOTNOTES} />
        </Container>
      </Section>

      {/* EMAIL UPDATES */}
      <Section tone="raised" spacing="tight">
        <Container width="prose" className="text-center">
          <Eyebrow>Field notes</Eyebrow>
          <Display as="h2" scale="sm" className="mt-3">
            New themes, new features, the occasional changelog.
          </Display>
          <p className="mt-4 text-body text-coffee-700 dark:text-cream-200">
            One email a month, sometimes less. Never more.
          </p>
          {emailSubmitted ? (
            <p className="mt-8 font-mono text-eyebrow uppercase text-accent-500">
              Subscribed. Check your inbox.
            </p>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try { await subscribeEmail(email, "landing"); } catch {}
                setEmailSubmitted(true);
                setEmail("");
              }}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-12 px-4 bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 rounded text-body text-coffee-900 dark:text-cream-50 placeholder-latte-500 focus:outline-none focus:border-coffee-900 dark:focus:border-cream-50"
                required
              />
              <Button type="submit" size="lg">Subscribe</Button>
            </form>
          )}
        </Container>
      </Section>

      {/* CTA FOOTER */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="card" className="mx-auto max-w-3xl">
            Make a card you actually want to hand out.
          </Display>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            {isSignedIn ? (
              <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                Open my card
              </Button>
            ) : (
              <SignUpButton mode="modal">
                <Button size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                  Claim my handle
                </Button>
              </SignUpButton>
            )}
            <Button to="/how-it-works" variant="secondary" size="lg" onInk>
              See how it works
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

export default Landing;

/* ---------------------------------------------------------------- */
/*  Sub-components — kept inline. They are landing-specific scenery. */
/* ---------------------------------------------------------------- */

// Fallback link target if demo-card resolution fails: the founder's account
// handle, which resolves to the account's active card (never a dead link).
const FOUNDER_HANDLE = "seanthomas2002";

function PreviewCard() {
  // D-001: resolve the founder's specific "Founder WhoBrew" card by its
  // owner-only label (server-side, anchored on the label — not a hardcoded id)
  // and link to the specific-card view so the demo always opens THAT card,
  // regardless of which card the account currently has active. Falls back to
  // the account handle if resolution fails, so the link is never dead.
  const [demoTo, setDemoTo] = useState<string>(`/card/${FOUNDER_HANDLE}`);

  useEffect(() => {
    let cancelled = false;
    fetchDemoCard()
      .then((card) => {
        if (!cancelled && card?.id) setDemoTo(`/card/id/${card.id}`);
      })
      .catch(() => {
        /* keep the handle fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative">
      {/* The founder's real card, used as a live demo. Clicking through opens the
          founder's specific "Founder WhoBrew" card (D-001). Only public,
          non-personal info is shown here — contact items are generic CTAs,
          never a real phone or email. */}
      <Link
        to={demoTo}
        aria-label="View Sean Thomas's live WhoBrew card"
        className="block group"
      >
        <Card tone="flat" padded={false} className="overflow-hidden border border-latte-300 dark:border-latte-800 max-w-sm mx-auto transition-shadow group-hover:shadow-lg">
          <div className="bg-coffee-900 dark:bg-cream-100 h-24" />
          <div className="text-center px-6 pb-6 -mt-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-coffee-700 flex items-center justify-center text-cream-50 font-display text-2xl border-4 border-cream-50 dark:border-coffee-900">
              ST
            </div>
            <h3 className="mt-3 font-display text-headline text-coffee-900 dark:text-cream-50">
              Sean Thomas
            </h3>
            <p className="text-body-sm text-coffee-700 dark:text-cream-200">Founder, WhoBrew</p>
          </div>
          <div className="px-5 pb-5 space-y-2">
            {[
              { i: <Linkedin className="w-3.5 h-3.5" />, t: "LinkedIn" },
              { i: <Calendar className="w-3.5 h-3.5" />, t: "Book a meeting" },
            ].map((l) => (
              <div
                key={l.t}
                className="flex items-center gap-3 px-3 py-3 rounded bg-cream-100 dark:bg-coffee-800 border border-latte-300 dark:border-latte-800"
              >
                <span className="w-7 h-7 rounded bg-cream-50 dark:bg-coffee-900 text-coffee-900 dark:text-cream-50 flex items-center justify-center">
                  {l.i}
                </span>
                <span className="text-body-sm text-coffee-900 dark:text-cream-50">{l.t}</span>
                <ArrowUpRight className="w-3.5 h-3.5 ml-auto text-coffee-600 dark:text-cream-200" />
              </div>
            ))}
            <span className="flex items-center justify-center gap-1.5 pt-2 font-mono text-caption text-coffee-700 dark:text-cream-200 group-hover:text-coffee-900 dark:group-hover:text-cream-50">
              View live card
              <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Card>
      </Link>
      {/* Sample visit counter — illustrative, not live data */}
      <div className="absolute -top-3 -right-3 sm:-right-6 bg-coffee-900 dark:bg-cream-100 text-cream-50 dark:text-coffee-900 px-3 py-2 font-mono text-eyebrow uppercase">
        342 visits · sample
      </div>
    </div>
  );
}

function BrowserChrome({ url }: { url: string }) {
  return (
    <div className="-m-6 sm:-m-8 mb-0 sm:mb-0 px-4 py-3 border-b border-latte-300 dark:border-latte-800 bg-cream-100 dark:bg-coffee-800 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-latte-300 dark:bg-latte-700" />
      <span className="w-2 h-2 rounded-full bg-latte-300 dark:bg-latte-700" />
      <span className="w-2 h-2 rounded-full bg-latte-300 dark:bg-latte-700" />
      <span className="ml-3 font-mono text-caption text-coffee-600 dark:text-cream-200">{url}</span>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="pt-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-coffee-700 flex items-center justify-center text-cream-50 font-display">ST</div>
        <div>
          <p className="font-display text-title text-coffee-900 dark:text-cream-50">Sean Thomas</p>
          <p className="font-mono text-caption text-coffee-600 dark:text-cream-200">Founder, WhoBrew</p>
        </div>
        <span className="ml-auto font-mono text-eyebrow uppercase text-success-700 bg-success-50 px-2 py-1 rounded">
          Live
        </span>
      </div>
      <Hairline />
      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "Visits", v: "1,284" },
          { k: "Clicks", v: "603" },
          { k: "Saved",  v: "147" },
        ].map((s) => (
          <div key={s.k}>
            <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200">{s.k}</p>
            <p className="mt-1 font-display text-3xl text-coffee-900 dark:text-cream-50 tabular-nums">{s.v}</p>
          </div>
        ))}
      </div>
      <Hairline />
      <div className="space-y-2">
        {[
          { i: <Linkedin className="w-3.5 h-3.5" />, t: "LinkedIn",         c: 64 },
          { i: <Calendar className="w-3.5 h-3.5" />, t: "Book a meeting",   c: 87 },
          { i: <Globe    className="w-3.5 h-3.5" />, t: "Portfolio",         c: 41 },
        ].map((l) => (
          <div key={l.t} className="flex items-center gap-3 px-3 py-2.5">
            <span className="w-7 h-7 rounded bg-cream-100 dark:bg-coffee-800 text-coffee-900 dark:text-cream-50 flex items-center justify-center border border-latte-300 dark:border-latte-800">
              {l.i}
            </span>
            <span className="text-body-sm text-coffee-900 dark:text-cream-50 flex-1">{l.t}</span>
            <span className="font-mono text-caption text-coffee-600 dark:text-cream-200">{l.c} clicks</span>
          </div>
        ))}
      </div>
      <p className="font-mono text-caption text-coffee-600 dark:text-cream-200">
        Example data — your own numbers appear once you sign up.
      </p>
    </div>
  );
}

function InsightsMockup() {
  const bars = [28, 32, 24, 48, 36, 64, 56, 80, 72, 92, 84, 100, 88, 96];
  return (
    <div className="pt-6 space-y-6">
      <div className="grid grid-cols-3 gap-6">
        {[
          { k: "Visits this week", v: "1,284", d: "+18% vs last week" },
          { k: "Unique visitors",  v: "892",   d: "+12% vs last week" },
          { k: "Saved to contacts", v: "147",  d: "+24% vs last week" },
        ].map((s) => (
          <div key={s.k}>
            <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200">{s.k}</p>
            <p className="mt-1 font-display text-3xl text-coffee-900 dark:text-cream-50 tabular-nums">{s.v}</p>
            <p className="font-mono text-caption text-success-700 mt-1">{s.d}</p>
          </div>
        ))}
      </div>
      <Hairline />
      <div className="h-32 flex items-end gap-1">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-coffee-900 dark:bg-cream-100"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <p className="font-mono text-caption text-coffee-600 dark:text-cream-200">
        Visits by day, last 14 days. Illustrative example — not real data.
      </p>
    </div>
  );
}

function ComparisonRow({ text, inverted = false }: { text: string; inverted?: boolean }) {
  return (
    <li className="flex gap-3">
      <span className={`font-mono shrink-0 ${inverted ? "text-accent-400 dark:text-accent-500" : "text-coffee-600 dark:text-cream-200"}`}>·</span>
      <span>{text}</span>
    </li>
  );
}

function PricingTile({ name, price, suffix, caption }: { name: string; price: string; suffix?: string; caption: string }) {
  return (
    <div className="bg-cream-50 dark:bg-coffee-900 p-8 sm:p-10">
      <Eyebrow>{name}</Eyebrow>
      <p className="mt-4 font-display text-display-sm text-coffee-900 dark:text-cream-50 tracking-tight">
        {price}
        {suffix && <span className="text-headline text-coffee-700 dark:text-cream-200">{suffix}</span>}
      </p>
      <p className="mt-4 text-body-sm text-coffee-700 dark:text-cream-200">{caption}</p>
    </div>
  );
}
